import { computed, inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { CallOptions, ConnectError, createClient } from '@connectrpc/connect';
import { createGrpcWebTransport } from '@connectrpc/connect-web';
import { PlatformIdInterceptor } from '@core/interceptors/platform-id';
import { environment } from '@env/environment';
import { AuthData } from '@gen/src/protos/auth/v1/auth_pb';

@Injectable({ providedIn: 'root' })
export class AuthStateService {
  private _token = signal<string | undefined>(undefined);
  private _refreshToken = signal<string | undefined>(undefined);
  private _temporaryToken = signal<string | undefined>(undefined);
  private router = inject(Router);
  private refreshPromise: Promise<boolean> | undefined = undefined;

  readonly token = computed(
    () =>
      this._token() ?? localStorage.getItem('auth_token') ?? this.readStorageToken() ?? undefined,
  );
  readonly refreshToken = computed(
    () => this._refreshToken() ?? localStorage.getItem('refresh_token') ?? undefined,
  );
  readonly temporaryToken = computed(
    () => this._temporaryToken() ?? localStorage.getItem('temporary_token') ?? undefined,
  );
  readonly transport = createGrpcWebTransport({
    baseUrl: environment.datasourceUrls.aprendeSso,
    useBinaryFormat: true,
    interceptors: [PlatformIdInterceptor],
  });
  readonly authClient = createClient(AuthData, this.transport);

  isAuthenticated = computed(() => !!this.token());

  constructor() {
    this._token.set(this.readStorageToken());
  }

  setToken(t: string) {
    this._token.set(t);
    localStorage.setItem('auth_token', t);
  }

  setTemporaryToken(t: string) {
    this._temporaryToken.set(t);
    localStorage.setItem('temporary_token', t);
  }

  setRefreshToken(t: string) {
    this._refreshToken.set(t);
    localStorage.setItem('refresh_token', t);
  }

  loadTokenFromStorage() {
    const t = localStorage.getItem('auth_token');
    if (t) this._token.set(t);
  }

  loadTemporaryTokenFromStorage() {
    const t = localStorage.getItem('temporary_token');
    if (t) this._temporaryToken.set(t);
  }

  loadRefreshTokenFromStorage() {
    const t = localStorage.getItem('refresh_token');
    if (t) this._refreshToken.set(t);
  }

  clear(option: string) {
    this._temporaryToken.set(undefined);
    switch (option) {
      case 'temporary':
        this._token.set(undefined);
        localStorage.removeItem('temporary_token');
        break;
      case 'all':
        this._token.set(undefined);
        this._temporaryToken.set(undefined);
        this._refreshToken.set(undefined);
        localStorage.removeItem('auth_token');
        localStorage.removeItem('temporary_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('asso-h-c');
        break;
    }
  }

  async logout() {
    const options: CallOptions = {
      headers: [['Authorization', `Bearer ${this._token()}`]],
    };
    try {
      const response = await this.authClient.localLogout({}, options);
      if (response.code === 'AUTH-105') {
        this.clear('all');
        this.router.navigate(['/login']);
      } else if (
        ['AUTH-005', 'AUTH-007', 'AUTH-018', 'AUTH-019', 'AUTH-020'].includes(response.code!)
      ) {
        console.error('Error al cerrar sesión.');
        return false;
      }
      return;
    } catch (ex) {
      if (ex instanceof ConnectError) {
        if (ex.code === 16) {
          this.clear('all');
          this.router.navigate(['/login']);
        }
      }
      return;
    }
  }

  refreshTokens(): Promise<boolean> {
    if (!this._refreshToken) {
      return Promise.resolve(false);
    }

    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    const refreshPromise = (async () => {
      try {
        const response = await this.authClient.refreshSession({
          refreshToken: this._refreshToken() ?? undefined,
        });

        if (response.code === 'AUTH-114') {
          if (response.token == undefined && response.refreshToken == undefined) {
            throw console.error('No token or refresh token in response.');
          }
          this.setToken(response.token!);
          this.setRefreshToken(response.refreshToken!);
        } else if (response.code === 'AUTH-004') {
          console.error('Error al refrescar tokens');
          return false;
        }

        return true;
      } catch (error) {
        console.error('Error al refrescar tokens', error);
        this.clear('all');
        return false;
      } finally {
        this.refreshPromise = undefined;
      }
    })();

    this.refreshPromise = refreshPromise;

    return refreshPromise;
  }

  getJwtClaim<T = unknown>(claim: string): T | null {
    try {
      if (this.token() === null) {
        return null;
      }
      const [, payloadB64Url] = this.token()!.split('.');
      if (!payloadB64Url) return null;

      const payloadB64 = payloadB64Url.replace(/-/g, '+').replace(/_/g, '/');
      const pad = payloadB64.length % 4;
      const padded = payloadB64 + (pad ? '='.repeat(4 - pad) : '');

      const json = atob(padded);
      const payload = JSON.parse(json) as Record<string, unknown>;

      return (payload[claim] as T) ?? null;
    } catch {
      return null;
    }
  }

  private readStorageToken(): string | undefined {
    const t = localStorage.getItem('auth_token');
    if (!t || t === 'undefined' || t === 'null') return undefined;
    return t;
  }
}
